import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ToastService } from "ng-zorro-antd-mobile";
import { firstValueFrom, Observable } from "rxjs";
import { API_SERVICE_PREFIX } from "src/app/app.constants";
import { AuthServerProvider } from "src/app/core/auth/auth-jwt.service";
import { Principal } from "src/app/core/auth/principal.service";
import { StateStorageService } from "src/app/core/auth/state-storage.service";
import { CurrentDeviceService } from "src/app/core/services/current-device.service";
import { GoogleAuthResult, GoogleRegisterVM } from "../google-oauth/google-oauth.types";
import { AuthContextService } from "../auth-context.service";
import { ModalAlertService } from "src/app/core/services/modal-alert.service";
import { TranslateService } from "@ngx-translate/core";
import { GoogleOauthService } from "../google-oauth/google-oauth.service";
import { DOCUMENT } from "@angular/common";

declare var google: any;

@Injectable({ providedIn: 'root' })
export class GoogleOneTapSignInService {

  defaultReturnUrl = '/home';

  constructor(
    private currentDeviceService: CurrentDeviceService,
    private http: HttpClient,
    private authServerProvider: AuthServerProvider,
    private authContextService: AuthContextService,
    private principalService: Principal,
    private stateStorageService: StateStorageService,
    private toast: ToastService,
    private router: Router,
    private modalAlertService: ModalAlertService,
    private translateService: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    private googleAuthService: GoogleOauthService,
  ) { }

  async tryInitializeGoogleOneTap(retries = 5) {
    if (this.principalService.isAuthenticated()) {
      return;
    }

    if (this.currentDeviceService.isWX()) {
      return;
    }

    if (typeof google !== 'undefined' && google.accounts && google.accounts.id) {
      await this.initializeGoogleOneTap();
    } else if (retries > 0) {
      setTimeout(() => this.tryInitializeGoogleOneTap(retries - 1), 500);
    } else {
      window.addEventListener('load', () => this.initializeGoogleOneTap(), { once: true });
    }
  }

  private async initializeGoogleOneTap() {
    const clientId = await this.loadGoogleClientId();
    if (!clientId) {
      return;
    }
    google.accounts.id.initialize({
      client_id: clientId,
      callback: this.handleCredentialResponse.bind(this),
      use_fedcm_for_prompt: true,
    });

    google.accounts.id.prompt();
  }

  handleCredentialResponse(response: any): void {
    this.oneTapLogin(response.credential, this.authContextService.returnUrl).subscribe({
      next: (authResult: GoogleAuthResult) => {
        if (authResult && authResult.id_token) {
          this.authServerProvider.authenticateSuccess(authResult, true);
          this.principalService.identity(true).then(() => {
            this.toast.success('Logged in successfully', 3000);
            this.router.navigateByUrl(this.authContextService.returnUrl, { replaceUrl: true });
          });
        } else if (authResult && authResult.requiresRegistration) {
          this.modalAlertService.open(
            this.translateService.instant('GENERAL.TIPS') || '提示',
            this.translateService.instant('AUTH.GOOGLE_REGISTER_CONFIRM_MESSAGE') || 'Your Google account is not registered. Do you want to proceed with registration?',
            {
              successBtnTitle: this.translateService.instant('GENERAL.REGISTER') || '注册',
              cancelBtnTitle: this.translateService.instant('GENERAL.CANCEL_BTN_TEXT') || '取消'
            }
          ).then(
            confirm => {
              if (confirm === 'confirmed') {
                this.registerWithOtp(authResult.otp, this.authContextService.returnUrl, this.authContextService.referralCode);
              } else {
                this.router.navigate(['/auth/sign-in'], { replaceUrl: true });
              }
            },
            () => this.router.navigate(['/auth/sign-in'], { replaceUrl: true })
          );
        } else {
            this.toast.fail('Login with Google failed', 3000);
            this.router.navigate(['/auth/sign-in']);
        }
      },
      error: error => {
        this.toast.fail('Login with Google failed', 3000);
        this.router.navigate(['/auth/sign-in']);
      }
    });
  }

  private loadGoogleClientId(): Promise<string> {
      const promise = firstValueFrom(this.getGoogleClientId())
          .then(response => {
              if (response && response.clientId) {
                  return response.clientId;
              } else {
                  return null;
              }
          })
          .catch(err => {
              console.error('Failed to load Google Client ID', err);
              return null;
          });
      return promise;
  }

  private getGoogleClientId(): Observable<{ clientId: string }> {
      return this.http.get<{ clientId: string }>(`${API_SERVICE_PREFIX}/user/api/authenticate/google/one-tap/client-id`, {} );
  }

  private oneTapLogin(token: string, redirectUrl: string): Observable<GoogleAuthResult> {
      const body = {
        idToken: token,
        redirectUrl
      }
      return this.http.post<GoogleAuthResult>(`${API_SERVICE_PREFIX}/user/api/authenticate/google/one-tap/login`, body, {});
  }

  registerWithOtp(otp: string, returnUrl: string, referralCode?: string) {
    returnUrl = returnUrl || this.stateStorageService.getUrl() || this.defaultReturnUrl;
    const redirectUrl = this.document.location.origin + '/auth/google/callback';
    const googleRegisterData: GoogleRegisterVM = {
      otp,
      redirectUrl,
      referralCode,
      loginIfRegistered: true
    };

    const tempSub = this.googleAuthService.registerWithGoogle(googleRegisterData)
      .subscribe({
        next: (response) => {
          if (response && response.id_token) {
            this.authServerProvider.authenticateSuccess(response, true);
            this.principalService.identity(true).then(() => {
              this.authContextService.clear();
              this.toast.success(this.translateService.instant('AUTH.REGISTRATION_SUCCESS') || '注册成功', 1500);
              this.router.navigateByUrl(returnUrl, { replaceUrl: true });
            });
          } else {
            this.toast.info(this.translateService.instant('AUTH.REGISTER_ERROR_MESSAGE') || '注册失败', 3000);
          }
        },
        error: (err) => {
          this.toast.info((this.translateService.instant('AUTH.REGISTER_ERROR_MESSAGE') || '注册失败：') + err.msg, 3000);
        this.router.navigate(['/auth/sign-up'], { replaceUrl: true });
        }
      });
  }
}
